import React, { useState } from "react";
import { Card, Button, Progress, Table, Tag, Alert } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone, QuestionCircleTwoTone, LoadingOutlined, HourglassOutlined } from "@ant-design/icons";
import PageLayoutWrapper from "components/PageLayoutWrapper";
import { useAuth } from "services/Auth";
import { syncHandler } from "utils/offlineCache";
import { formatBytes, safeParseFloat } from "@salespark/toolkit";
const delay = (ms) => new Promise((res) => setTimeout(res, ms));

const createDummySendEntries = (count = 20) => {
  const methods = ["POST", "PUT", "DELETE"];
  const urls = ["/services", "/services", "/services", "/services", "/payments"];
  const buildData = (index) => {
    const minBytes = 50;
    const maxBytes = 50000;
    const baseData = {
      reference: `REF-${String(index + 1).padStart(4, "0")}`,
      clientName: `Cliente ${index + 1}`,
      amount: Math.floor(Math.random() * 4951) + 50,
      notes: `Obs ${Math.floor(Math.random() * 100000)}`,
      items: Array.from({ length: (index % 4) + 1 }, (_, itemIndex) => ({
        itemId: `${index + 1}-${itemIndex + 1}`,
        description: `Item ${itemIndex + 1}`,
        quantity: (itemIndex % 5) + 1,
      })),
    };

    let serialized = JSON.stringify(baseData);
    if (serialized.length > maxBytes) {
      baseData.notes = baseData.notes.slice(0, 20);
      baseData.items = baseData.items.slice(0, 1);
      serialized = JSON.stringify(baseData);
      if (serialized.length > maxBytes) {
        delete baseData.notes;
        serialized = JSON.stringify(baseData);
      }
    }

    const targetBytes = Math.floor(Math.random() * (maxBytes - minBytes + 1)) + minBytes;
    const baseLength = JSON.stringify({ ...baseData, padding: "" }).length;
    const paddingLength = Math.max(0, targetBytes - baseLength - 2);
    if (paddingLength > 0) {
      baseData.padding = "x".repeat(paddingLength);
      serialized = JSON.stringify(baseData);
      if (serialized.length > maxBytes) {
        baseData.padding = baseData.padding.slice(0, Math.max(0, paddingLength - (serialized.length - maxBytes)));
      }
    }

    return baseData;
  };
  return Array.from({ length: count }, (_, index) => {
    const method = methods[index % methods.length];
    return {
      id: index + 1,
      url: urls[index % urls.length],
      method,
      data: buildData(index),
      status: "pending",
    };
  });
};

const Sync = () => {
  const { auth } = useAuth();
  const apiClient = syncHandler(auth.data);
  const [isLoading, setIsLoading] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [getProgress, setGetProgress] = useState(0);

  const [getEndpoints, setGetEndpoints] = useState([
    { id: 1, url: "/services", options: {}, label: "Lista de Serviços", status: "pending" },
    { id: 2, url: "/services", options: {}, label: "Lista de Serviços", status: "pending" },
    { id: 3, url: "/services", options: {}, label: "Lista de Serviços", status: "pending" },
    { id: 4, url: "/services", options: {}, label: "Lista de Serviços", status: "pending" },
    { id: 5, url: "/services", options: {}, label: "Lista de Serviços", status: "pending" },
  ]);
  const [sendEntries, setSendEntries] = useState(createDummySendEntries);

  const handleSync = async () => {
    setIsLoading(true);

    setSendProgress(0);
    setGetProgress(0);

    // Set all send entries to syncing
    setSendEntries((prevEntries) => prevEntries.map((entry) => ({ ...entry, status: "syncing" })));
    let counter = 0;
    for (const entry of sendEntries) {
      counter++;
      const response = await apiClient.get(entry.url, entry.options);
      if (!response.status) {
        setSendEntries((prevEntries) => prevEntries.map((ep) => (ep.id === entry.id ? { ...ep, status: "failed" } : ep)));
      } else {
        setSendEntries((prevEntries) => prevEntries.map((ep) => (ep.id === entry.id ? { ...ep, status: "completed" } : ep)));
      }

      const percent = safeParseFloat((counter / sendEntries.length) * 100, 2);
      setSendProgress(percent);
      await delay(50); // Simulate delay for demo purposes
    }

    // remove all completed send entries
    setSendEntries((prevEntries) => prevEntries.filter((entry) => entry.status !== "completed"));

    // Set all GET endpoints to syncing
    setGetEndpoints((prevEndpoints) => prevEndpoints.map((ep) => ({ ...ep, status: "syncing" })));
    counter = 0;
    for (const endpoint of getEndpoints) {
      counter++;
      const response = await apiClient.get(endpoint.url, endpoint.options);
      if (!response.status) {
        setGetEndpoints((prevEndpoints) => prevEndpoints.map((ep) => (ep.id === endpoint.id ? { ...ep, status: "failed" } : ep)));
      } else {
        setGetEndpoints((prevEndpoints) => prevEndpoints.map((ep) => (ep.id === endpoint.id ? { ...ep, status: "completed" } : ep)));
      }

      const percent = safeParseFloat((counter / getEndpoints.length) * 100, 2);
      setGetProgress(percent);
      await delay(500); // Simulate delay for demo purposes
    }

    // remove all completed get endpoints
    setGetEndpoints((prevEndpoints) => prevEndpoints.filter((ep) => ep.status !== "completed"));

    setIsLoading(false);
  };

  const getColumns = [
    {
      title: "Nome",
      dataIndex: "label",
    },
    {
      title: "Endpoint",
      dataIndex: "url",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      align: "center",
      render: (_, record) => {
        if (record.status === "pending") return <HourglassOutlined />;
        if (record.status === "syncing") return <LoadingOutlined />;
        if (record.status === "failed") return <CloseCircleTwoTone twoToneColor={"#ff6c52"} />;
        if (record.status === "completed") return <CheckCircleTwoTone twoToneColor={"#84ff6b"} />;
        return <QuestionCircleTwoTone />;
      },
    },
  ];

  const sendColumns = [
    {
      title: "Ordem",
      dataIndex: "id",
      width: 60,
      align: "center",
    },
    {
      title: "Método",
      dataIndex: "method",
      width: 110,
      render: (_, record) => {
        if (record.method === "POST") return <Tag color="green">POST</Tag>;
        if (record.method === "PUT") return <Tag color="blue">PUT</Tag>;
        if (record.method === "DELETE") return <Tag color="red">DELETE</Tag>;
        return <Tag>{record.method}</Tag>;
      },
    },
    {
      title: "Endpoint",
      dataIndex: "url",
    },
    {
      title: "Tamanho Dados",
      dataIndex: "data",
      align: "right",
      width: 160,
      render: (_, record) => {
        return formatBytes(JSON.stringify(record.data).length, false, 2, true);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      align: "center",
      render: (_, record) => {
        if (record.status === "pending") return <HourglassOutlined />;
        if (record.status === "syncing") return <LoadingOutlined />;
        if (record.status === "failed") return <CloseCircleTwoTone twoToneColor={"#ff6c52"} />;
        if (record.status === "completed") return <CheckCircleTwoTone twoToneColor={"#84ff6b"} />;
        return <QuestionCircleTwoTone />;
      },
    },
  ];
  const allGetCompleted = getEndpoints.every((ep) => ep.status === "completed");
  const allSendCompleted = sendEntries.every((ep) => ep.status === "completed");

  return (
    <PageLayoutWrapper title="Sincronizar dados">
      {isLoading && <Alert description="Sincronização em andamento. Mantenha-se nesta página." type="info" showIcon className="mb-3" />}
      <Card
        title="Sincronização de Dados"
        type="inner"
        extra={
          <Button type="primary" style={{ marginTop: 16 }} onClick={handleSync} loading={isLoading}>
            Iniciar Sincronização
          </Button>
        }
      >
        <div>
          <span>Envio dados:</span>
          <Progress percent={sendProgress} status={isLoading ? "active" : "normal"} />
        </div>
        <div className="mt-3">
          <span>Receber dados:</span>
          <Progress percent={getProgress} status={isLoading ? "active" : "normal"} />
        </div>
      </Card>
      <Card
        title={`Enviar dados (${sendEntries.length})`}
        extra={
          <>
            <Tag color="green">{sendEntries.filter((el) => el.status === "completed").length}</Tag>
            <Tag color="red">{sendEntries.filter((el) => el.status === "failed").length}</Tag>
          </>
        }
      >
        {allSendCompleted ? (
          <div className="flex-space-between-center">
            <h4>Sincronização concluída com sucesso!</h4>
            <div>
              <CheckCircleTwoTone twoToneColor={"#84ff6b"} style={{ fontSize: 20 }} />
            </div>
          </div>
        ) : (
          <Table size="small" columns={sendColumns} dataSource={sendEntries} pagination={false} />
        )}
      </Card>
      <Card
        title={`Receber dados (${getEndpoints.length})`}
        extra={
          <>
            <Tag color="green">{getEndpoints.filter((el) => el.status === "completed").length}</Tag>
            <Tag color="red">{getEndpoints.filter((el) => el.status === "failed").length}</Tag>
          </>
        }
      >
        {allGetCompleted ? (
          <div className="flex-space-between-center">
            <h4>Sincronização concluída com sucesso!</h4>
            <div>
              <CheckCircleTwoTone twoToneColor={"#84ff6b"} style={{ fontSize: 20 }} />
            </div>
          </div>
        ) : (
          <Table size="small" columns={getColumns} dataSource={getEndpoints} pagination={false} />
        )}
      </Card>
    </PageLayoutWrapper>
  );
};

export default Sync;
